import { useEffect, useMemo, useRef, useState } from 'react';
import type { DeadlineItem } from '@cg-projects/shared';
import { useDashboardStore } from '../store/dashboardStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useUiStore } from '@/core/ui/uiStore';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { patchTask } from '@/features/tasks/mutations';
import { AssigneeStack, Skeleton } from '@/core/ui/atoms';
import { Icon } from '@/core/ui/icons';
import { initials, avatarColor } from '@/core/ui/helpers';
import { Ban, CalendarDays, ChevronDown, ChevronRight, Plus, Star, Target } from 'lucide-react';

const ROWS_PER_PROJECT = 6;

function midnight(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function daysToToday(iso: string | null): number {
  if (!iso) return 99999;
  // Bare "YYYY-MM-DD" has no time part — `new Date()` treats that specific
  // form as UTC midnight, which reads as the previous day anywhere west of
  // UTC (Florida is UTC-4/-5). Appending T00:00:00 makes it a date-TIME
  // form instead, which Date parses in local time. Same fix already used
  // by grouping.ts:31, helpers.ts:43 and MyWork.tsx:46 — matched here.
  const hasTime = iso.includes('T') || iso.includes(' ');
  const d = new Date(hasTime ? iso.replace(' ', 'T') : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 99999;
  return Math.round((midnight(d).getTime() - midnight(new Date()).getTime()) / 86_400_000);
}
/** Humanized, relevance-signalling due label. */
function relDue(iso: string | null): { label: string; cls: string } {
  const days = daysToToday(iso);
  if (days === 99999) return { label: '—', cls: 'text-muted' };
  if (days === 0) return { label: 'Today', cls: 'text-warn' };
  if (days > 0) return { label: days === 1 ? 'Tomorrow' : `in ${days}d`, cls: days <= 7 ? 'text-warn' : 'text-muted' };
  const o = -days;
  const label = o < 14 ? `${o}d overdue` : o < 60 ? `${Math.round(o / 7)}w overdue` : o < 700 ? `${Math.round(o / 30)}mo overdue` : `${Math.round(o / 365)}y overdue`;
  return { label, cls: 'text-danger' };
}

/** Local YYYY-MM-DD, n days back. Built by hand, NOT via toISOString(): the
 *  Reports date inputs are bare local dates, and a UTC round-trip shifts the day
 *  anywhere west of UTC — the same trap daysToToday guards against, in reverse. */
function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// --- The delta strip ---------------------------------------------------------
// "Since Friday: +14 completed · +3 new overdue" — the app noticed you were
// gone. A per-user snapshot of the headline numbers lives in localStorage and
// the next visit diffs the fresh feed against it. Nothing touches the server.

type HomeSnapshot = { ts: number; open: number; overdue: number; done: number; stuck: number };

const snapshotKey = (uid: number) => `cgp:home:last:${uid}`;
const SNAPSHOT_MIN_AGE_MS = 30 * 60 * 1000; // younger than this = same sitting, no strip

function readSnapshot(uid: number): HomeSnapshot | null {
  try {
    const raw = localStorage.getItem(snapshotKey(uid));
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<HomeSnapshot>;
    // A malformed snapshot (schema drift, hand-edited storage) must read as
    // "no snapshot", never crash the front page.
    if (typeof s.ts !== 'number' || typeof s.open !== 'number' || typeof s.overdue !== 'number'
      || typeof s.done !== 'number' || typeof s.stuck !== 'number') return null;
    return { ts: s.ts, open: s.open, overdue: s.overdue, done: s.done, stuck: s.stuck };
  } catch { return null; }
}

function writeSnapshot(uid: number, snap: HomeSnapshot) {
  try { localStorage.setItem(snapshotKey(uid), JSON.stringify(snap)); } catch { /* ignore */ }
}

/** "this morning" / "yesterday" / "Friday" — when the snapshot was taken.
 *  Past a week a weekday name turns ambiguous, so it falls back to the date. */
function sinceLabel(ts: number): string {
  const then = new Date(ts);
  const days = Math.round((midnight(new Date()).getTime() - midnight(then).getTime()) / 86_400_000);
  if (days <= 0) return 'this morning';
  if (days === 1) return 'yesterday';
  if (days < 7) return then.toLocaleDateString('en-US', { weekday: 'long' });
  return then.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

/** A stat you can only read is a report; one you can open is a cockpit. Every
 *  tile is a real button into the filtered work behind its number. */
function Stat({ label, value, tone, title, onClick }: {
  label: string; value: number; tone?: 'danger' | 'warn' | 'default'; title?: string; onClick?: () => void;
}) {
  const color = tone === 'danger' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return (
    <button type="button" onClick={onClick} title={title} className="rounded-lg border border-line bg-canvas px-4 py-3 text-left transition hover:border-brand/50">
      <div className={`text-2xl font-semibold tabular-nums ${color}`}>{value.toLocaleString()}</div>
      <div className="text-xs text-faint">{label}</div>
    </button>
  );
}

function DeadlineRow({ item, onReload, showArea }: { item: DeadlineItem; onReload: () => void; showArea?: boolean }) {
  const open = useTaskDetailStore((s) => s.open);
  const assignees = useLookupStore((s) => s.assignees);
  // The quick-assign <option> list (~one per person on the team) must not sit
  // in the DOM for every row on the page — dozens of rows × the whole roster is
  // thousands of nodes behind opacity-0. The options mount once intent is
  // shown: pointer enters the row or focus lands inside it (keyboard users tab
  // straight to the select). Both signals precede the click/keypress that opens
  // the dropdown, so the list can never be seen empty. Armed is one-way —
  // disarming on mouse leave would be re-render churn for nothing.
  const [armed, setArmed] = useState(false);
  const arm = () => { if (!armed) setArmed(true); };
  const available = assignees.filter((a) => !item.assignees.some((x) => x.id === a.id));
  const due = relDue(item.deadline);

  const assign = async (uid: number) => {
    if (await patchTask(item.taskId, { assigneeIds: [...item.assignees.map((a) => a.id), uid] })) onReload();
  };

  return (
    <div onClick={() => open(item.taskId)} onMouseEnter={arm} onFocusCapture={arm} className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-hover">
      <span className={`w-24 shrink-0 text-[11px] ${due.cls}`}>{due.label}</span>
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
        {item.priority === '1' && <Icon i={Star} size={14} className="mr-1 inline align-[-0.15em] fill-current text-warn" />}
        {item.name}
        {/* Inside a project group the project is the header; in the cross-project
            Today docket each row needs to say where it lives (StuckRow's recipe). */}
        {showArea && <span className="ml-2 text-[11px] text-faint">{item.areaName}</span>}
      </span>
      <div onClick={(e) => e.stopPropagation()} className="flex shrink-0 items-center gap-1">
        <AssigneeStack assignees={item.assignees} max={2} />
        {available.length > 0 && (
          <select
            value=""
            onChange={(e) => { if (e.target.value) void assign(Number(e.target.value)); }}
            title="Assign"
            className="w-6 rounded border border-line bg-canvas text-center text-[11px] text-muted opacity-0 transition group-hover:opacity-100 hover:text-ink"
          >
            <option value="">+</option>
            {/* Only the '+' placeholder exists until the row is armed — the
                select keeps its width/opacity styling either way, so nothing
                visual or focusable changes, just the DOM weight. */}
            {armed && available.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

function ProjectGroup({
  areaId, name, items, collapsed, onToggle, onReload,
}: {
  areaId: number; name: string; items: DeadlineItem[]; collapsed: boolean; onToggle: () => void; onReload: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const selectArea = useAreaStore((s) => s.selectArea);
  const overdue = items.filter((i) => i.bucket === 'overdue').length;
  const soon = items.length - overdue;
  const shown = showAll ? items : items.slice(0, ROWS_PER_PROJECT);

  return (
    <div className="border-b border-line last:border-0">
      <button onClick={onToggle} className="flex w-full items-center gap-2 px-2 py-2 text-left hover:bg-hover">
        <Icon i={collapsed ? ChevronRight : ChevronDown} size={14} className="text-faint" />
        <span className="truncate text-[13px] font-medium text-ink">{name}</span>
        <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[11px]">
          {overdue > 0 && <span className="rounded bg-danger/15 px-1.5 py-0.5 text-danger">{overdue} late</span>}
          {soon > 0 && <span className="rounded bg-warn-soft px-1.5 py-0.5 text-warn">{soon} soon</span>}
        </span>
      </button>
      {!collapsed && (
        <div className="pb-1">
          {shown.map((item) => <DeadlineRow key={item.taskId} item={item} onReload={onReload} />)}
          {items.length > ROWS_PER_PROJECT && (
            <div className="px-2 pt-0.5">
              {!showAll ? (
                <button onClick={() => setShowAll(true)} className="text-[11px] text-muted hover:text-ink">+ {items.length - ROWS_PER_PROJECT} more…</button>
              ) : (
                <button onClick={() => selectArea(areaId)} className="text-[11px] text-brand hover:underline">Open {name} →</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StuckRow({ item }: { item: DeadlineItem }) {
  const open = useTaskDetailStore((s) => s.open);
  return (
    <div onClick={() => open(item.taskId)} className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-hover">
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
        {item.priority === '1' && <Icon i={Star} size={14} className="mr-1 inline align-[-0.15em] fill-current text-warn" />}
        {item.name}
        <span className="ml-2 text-[11px] text-faint">{item.areaName}</span>
      </span>
      <AssigneeStack assignees={item.assignees} max={2} />
    </div>
  );
}

function StuckPanel({ items }: { items: DeadlineItem[] }) {
  const [showAll, setShowAll] = useState(false);
  if (items.length === 0) return null;
  const shown = showAll ? items : items.slice(0, ROWS_PER_PROJECT);
  // The id is the Stuck tile's scroll target; scroll-mt keeps the panel from
  // landing flush against the viewport edge.
  return (
    <section id="stuck-panel" className="scroll-mt-4 overflow-hidden rounded-lg border border-danger/30 bg-(--color-danger-tint)/40">
      <div className="flex items-center gap-2 border-b border-danger/20 px-3 py-2">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-danger"><Icon i={Ban} size={14} /> Stuck</span>
        <span className="rounded bg-danger-soft px-1.5 py-0.5 text-[11px] text-danger">{items.length}</span>
        <span className="ml-auto text-[11px] text-faint">blocked / waiting — needs unblocking</span>
      </div>
      <div className="py-1">
        {shown.map((item) => <StuckRow key={item.taskId} item={item} />)}
        {items.length > ROWS_PER_PROJECT && (
          <div className="px-2 pt-0.5">
            <button onClick={() => setShowAll((v) => !v)} className="text-[11px] text-danger hover:underline">
              {showAll ? 'Show less' : `+ ${items.length - ROWS_PER_PROJECT} more…`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/** The Today docket: everything due exactly today, across projects, before
 *  anything else. Rows are the same DeadlineRow the groups below use — the
 *  data is already in the deadlines feed, so this costs zero extra fetches. */
function TodayDocket({ items, onReload }: { items: DeadlineItem[]; onReload: () => void }) {
  const [showAll, setShowAll] = useState(false);
  if (items.length === 0) return null;
  const shown = showAll ? items : items.slice(0, ROWS_PER_PROJECT);
  return (
    <section className="overflow-hidden rounded-lg border border-line bg-canvas">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-ink"><Icon i={CalendarDays} size={14} className="text-warn" /> Today</span>
        <span className="rounded bg-warn-soft px-1.5 py-0.5 text-[11px] tabular-nums text-warn">{items.length}</span>
        <span className="ml-auto text-[11px] text-faint">due today</span>
      </div>
      <div className="py-1">
        {shown.map((item) => <DeadlineRow key={item.taskId} item={item} onReload={onReload} showArea />)}
        {items.length > ROWS_PER_PROJECT && (
          <div className="px-2 pt-0.5">
            <button onClick={() => setShowAll((v) => !v)} className="text-[11px] text-muted hover:text-ink">
              {showAll ? 'Show less' : `+ ${items.length - ROWS_PER_PROJECT} more…`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export function Dashboard() {
  const data = useDashboardStore((s) => s.data);
  const loading = useDashboardStore((s) => s.loading);
  const error = useDashboardStore((s) => s.error);
  const load = useDashboardStore((s) => s.load);
  const selectArea = useAreaStore((s) => s.selectArea);
  const user = useAuthStore((s) => s.user);
  const loadLookups = useLookupStore((s) => s.load);
  const openCreateProject = useUiStore((s) => s.openCreateProject);
  const openMyWork = useUiStore((s) => s.openMyWork);
  const openReports = useUiStore((s) => s.openReports);

  // Always refresh on mount (cached data stays on screen — no spinner flash).
  useEffect(() => { void load(true); void loadLookups(); }, [load, loadLookups]);
  const reload = () => void load(true);

  // "Just mine" — the team asked to filter Home to their own work, not the whole
  // team's. On = the focused personal view (only tasks assigned to me; the manager
  // stat-tiles + Workload hide, so no all-project number sits next to a filtered
  // list). Persisted per browser.
  const myUid = user?.uid;
  const [mine, setMine] = useState<boolean>(() => {
    try { return localStorage.getItem('cgp:home:mine') === '1'; } catch { return false; }
  });
  const setMinePersist = (v: boolean) => {
    setMine(v);
    try { localStorage.setItem('cgp:home:mine', v ? '1' : '0'); } catch { /* ignore */ }
  };
  const stuckShown = useMemo(
    () => (mine && myUid !== undefined ? (data?.stuck ?? []).filter((d) => d.assignees.some((a) => a.id === myUid)) : (data?.stuck ?? [])),
    [data, mine, myUid],
  );

  // The delta strip's baseline is captured ONCE, at mount — the diff stays
  // pinned to the moment you arrived even though fresher snapshots get written
  // as you leave (tab hidden / unmount below).
  const [baseline] = useState<HomeSnapshot | null>(() => (myUid === undefined ? null : readSnapshot(myUid)));
  const snapRef = useRef<HomeSnapshot | null>(null);
  const uidRef = useRef(myUid);
  uidRef.current = myUid;
  const seededRef = useRef(false);

  // Keep the would-be snapshot current as the feed refreshes. On a first-ever
  // visit, write one silently and show nothing — the strip earns its first
  // appearance on the SECOND visit, when there is something to diff against.
  useEffect(() => {
    if (!data) return;
    snapRef.current = {
      ts: Date.now(),
      open: data.totals.open,
      overdue: data.totals.overdue,
      done: data.totals.done,
      stuck: (data.stuck ?? []).length,
    };
    if (baseline === null && myUid !== undefined && !seededRef.current) {
      seededRef.current = true;
      writeSnapshot(myUid, snapRef.current);
    }
  }, [data, myUid, baseline]);

  // Persist the freshest numbers when the user leaves — tab hidden or unmount —
  // so the next visit diffs against the state they last SAW, not stale data.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden' && uidRef.current !== undefined && snapRef.current) {
        writeSnapshot(uidRef.current, snapRef.current);
      }
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      if (uidRef.current !== undefined && snapRef.current) writeSnapshot(uidRef.current, snapRef.current);
    };
  }, []);

  // What changed while you were gone. A snapshot under 30 min old is the same
  // sitting — no strip. All chips zero/negative = nothing to report.
  const delta = useMemo(() => {
    if (!data || !baseline) return null;
    if (Date.now() - baseline.ts <= SNAPSHOT_MIN_AGE_MS) return null;
    const done = data.totals.done - baseline.done;
    const overdue = data.totals.overdue - baseline.overdue;
    const stuck = (data.stuck ?? []).length - baseline.stuck;
    if (done <= 0 && overdue <= 0 && stuck <= 0) return null;
    return { since: sinceLabel(baseline.ts), done, overdue, stuck };
  }, [data, baseline]);

  // Due exactly TODAY, across all projects — the docket. Honors the same
  // "Just mine" filter as the rest of the page.
  const todayItems = useMemo(() => {
    const items = (data?.deadlines ?? []).filter((d) => daysToToday(d.deadline) === 0);
    return mine && myUid !== undefined ? items.filter((d) => d.assignees.some((a) => a.id === myUid)) : items;
  }, [data, mine, myUid]);

  // Group "needs attention" by project, ranked by how behind each is; most-relevant items first.
  const groups = useMemo(() => {
    const m = new Map<number, { name: string; items: DeadlineItem[] }>();
    for (const d of data?.deadlines ?? []) {
      // Only filter once we actually know who "me" is — never hide everything
      // during the brief window before the user loads.
      if (mine && myUid !== undefined && !d.assignees.some((a) => a.id === myUid)) continue;
      const g = m.get(d.areaId) ?? { name: d.areaName || `Project #${d.areaId}`, items: [] };
      g.items.push(d);
      m.set(d.areaId, g);
    }
    return [...m.entries()]
      .map(([areaId, g]) => ({
        areaId,
        name: g.name,
        items: g.items.sort((a, b) => Math.abs(daysToToday(a.deadline)) - Math.abs(daysToToday(b.deadline))),
        overdue: g.items.filter((i) => i.bucket === 'overdue').length,
      }))
      .sort((a, b) => b.overdue - a.overdue || b.items.length - a.items.length);
  }, [data, mine, myUid]);

  // Auto-expand the most-behind project; collapse the rest.
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const groupKey = groups.map((g) => g.areaId).join(',');
  useEffect(() => {
    setCollapsed(new Set(groups.slice(1).map((g) => g.areaId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey]);
  const toggle = (id: number) => setCollapsed((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const firstName = user?.name?.split(/\s+/)[0] ?? 'there';
  // The Daily's anchor: Home's largest type belongs to *now*, not a lifetime
  // total. Computed at render — every store change re-renders, so a tab left
  // open doesn't hold yesterday's date past its next glance.
  const now = new Date();
  const dateLine = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const daypart = now.getHours() < 12 ? 'morning' : now.getHours() < 17 ? 'afternoon' : 'evening';
  if (loading && !data) {
    // The page's own shape, ghosted — masthead, five tiles, six rows, the rail.
    // A layout that loads in place beats a spinner that loads somewhere else.
    return (
      <div className="h-full overflow-y-auto px-8 py-6">
        <div aria-busy="true" aria-label="Building your overview…" className="mx-auto max-w-6xl space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-3 w-36" />
            <Skeleton className="h-8 w-64" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[72px]" />)}
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
            <div className="space-y-2">
              {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-9" />)}
            </div>
            <Skeleton className="h-72" />
          </div>
        </div>
      </div>
    );
  }

  // Brand-new user (no projects yet) — explain instead of showing a wall of zeros.
  if (data && data.projects.length === 0 && !user?.isAdmin) {
    return (
      <div className="flex h-full items-center justify-center px-8">
        <div className="max-w-md rounded-xl border border-line bg-canvas px-6 py-8 text-center">
          <div className="mb-2 text-2xl">👋</div>
          <h1 className="mb-2 text-lg font-semibold text-ink">Hi {firstName} — you're in!</h1>
          <p className="text-sm leading-relaxed text-muted">
            You don't have any projects yet. Pat or Allyson will add you to your team's
            projects, and they'll appear in the sidebar the moment that happens — no need
            to log out or refresh.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-8 py-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-[0.14em] text-brand">{dateLine}</div>
            <h1 className="mt-1 text-display font-semibold tracking-[-0.01em] text-ink">Good {daypart}, {firstName}</h1>
            {/* The delta strip: what happened at CG while you were gone. */}
            {delta && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-medium tabular-nums">
                <span className="font-normal text-faint">Since {delta.since}:</span>
                {delta.done > 0 && <span className="text-success">+{delta.done.toLocaleString()} completed</span>}
                {delta.overdue > 0 && <span className="text-danger">+{delta.overdue.toLocaleString()} new overdue</span>}
                {delta.stuck > 0 && <span className="text-danger">{delta.stuck.toLocaleString()} stuck</span>}
              </div>
            )}
            <p className="mt-1 text-sm text-muted">
              {mine ? 'Your tasks' : 'Your overview'} across {data?.projects.length ?? 0} projects.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Everyone = the full manager overview; Just mine = only work assigned to me. */}
            <div className="flex rounded-md border border-line p-0.5 text-xs">
              <button onClick={() => setMinePersist(false)} className={`rounded px-2 py-1 font-medium transition ${!mine ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`}>Everyone</button>
              <button onClick={() => setMinePersist(true)} className={`rounded px-2 py-1 font-medium transition ${mine ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`}>Just mine</button>
            </div>
            {/* Asked for twice: reach your own work and your own month from HOME,
                without knowing the sidebar has them. The sidebar entry stays — this
                is a second door, not a move. */}
            <button
              onClick={() => openMyWork('list')}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted transition hover:bg-hover hover:text-ink"
              title="Everything assigned to you, in your buckets"
            ><Icon i={Target} size={14} /> My Tasks</button>
            <button
              onClick={() => openMyWork('calendar')}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted transition hover:bg-hover hover:text-ink"
              title="Your work across every project, on one month"
            ><Icon i={CalendarDays} size={14} /> Calendar</button>
            <button onClick={openCreateProject} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-page transition hover:brightness-110 active:scale-[0.98]"><Icon i={Plus} size={14} /> New project</button>
          </div>
        </div>

        {error && <div className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{error}</div>}

        {data && (
          <>
            {!mine && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <Stat label="Open tasks" value={data.totals.open} title="All open work, in Reports" onClick={() => openReports(undefined, { states: ['open'] })} />
                {/* dateTo is inclusive server-side, so "yesterday" = strictly
                    before today — the same line the overdue bucket draws. */}
                <Stat label="Overdue" value={data.totals.overdue} tone="danger" title="Open work past its deadline, in Reports" onClick={() => openReports(undefined, { states: ['open'], dateBasis: 'deadline', dateTo: isoDaysAgo(1) })} />
                <Stat label="Stuck" value={(data.stuck ?? []).length} tone={(data.stuck?.length ?? 0) ? 'danger' : 'default'} title="Jump to the stuck list" onClick={() => document.getElementById('stuck-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
                <Stat label="Unassigned" value={data.unassignedOpen} tone="warn" title="Open work nobody owns, in Reports" onClick={() => openReports(undefined, { states: ['open'], assigneeId: 'none' })} />
                <Stat label="Completed" value={data.totals.done} title="Finished work, in Reports" onClick={() => openReports(undefined, { states: ['done'] })} />
              </div>
            )}

            {/* The output line — Home finally says whether the company shipped
                more this week than last (real dates, not the per-browser
                delta strip). Quiet chrome: one sentence, no chart. */}
            {!mine && data.trend && (data.trend.lastWeek > 0 || data.trend.priorWeek > 0) && (
              <div className="text-[13px] text-muted">
                Finished <button onClick={() => openReports(undefined, { states: ['done'], dateBasis: 'closed', dateFrom: isoDaysAgo(7) })} className="font-semibold text-ink underline-offset-2 hover:underline">{data.trend.lastWeek} {data.trend.lastWeek === 1 ? 'thing' : 'things'} last week</button>
                {data.trend.priorWeek > 0 && data.trend.lastWeek !== data.trend.priorWeek && (
                  <span className={data.trend.lastWeek > data.trend.priorWeek ? 'text-success' : 'text-warn'}>
                    {' '}— {Math.abs(data.trend.lastWeek - data.trend.priorWeek)} {data.trend.lastWeek > data.trend.priorWeek ? 'more' : 'fewer'} than the week before
                  </span>
                )}
                {data.trend.priorWeek === data.trend.lastWeek && data.trend.priorWeek > 0 && <span> — same as the week before</span>}
              </div>
            )}

            <StuckPanel items={stuckShown} />

            {/* The Today docket sits directly above "Needs attention": today's
                due work, first, before the by-project backlog. */}
            <TodayDocket items={todayItems} onReload={reload} />

            <div className={`grid grid-cols-1 gap-6 ${mine ? '' : 'lg:grid-cols-[1fr_300px]'}`}>
              <section>
                <div className="mb-2 flex items-baseline gap-2">
                  <h2 className="text-sm font-semibold text-ink">{mine ? 'Your tasks needing attention' : 'Needs attention'}</h2>
                  <span className="text-xs text-faint">by project · most behind first</span>
                  {/* The list caps out; the tiles above never do. Say which is which. */}
                  {data.deadlinesTruncated && (
                    <span className="text-xs text-warn" title="There is more overdue work than fits here — open the project boards for the full picture.">· showing the most recent</span>
                  )}
                </div>
                <div className="rounded-lg border border-line bg-canvas">
                  {groups.map((g) => (
                    <ProjectGroup key={g.areaId} areaId={g.areaId} name={g.name} items={g.items} collapsed={collapsed.has(g.areaId)} onToggle={() => toggle(g.areaId)} onReload={reload} />
                  ))}
                  {groups.length === 0 && (
                    <div className="px-2 py-6 text-center text-sm text-faint">
                      {mine
                        ? (data.deadlinesTruncated
                            ? 'Showing the most recent work — open your project boards for your full list.'
                            : 'Nothing of yours needs attention right now 🎉')
                        : 'Nothing overdue or due this week 🎉'}
                    </div>
                  )}
                </div>
              </section>

              {!mine && (
              <aside>
                <h2 className="mb-2 text-sm font-semibold text-ink">Workload</h2>
                {/* Says what the number counts. Before v1.27.0 it silently excluded
                    sub-tasks, so it disagreed with what each person sees in My Work —
                    one person showed 9 open items while holding 235. */}
                <div className="mb-1.5 text-[11px] text-faint">
                  Open work per person, sub-tasks included — in the projects you can see.
                </div>
                {data.workloadTruncated && (
                  <div className="mb-1.5 rounded border border-warn/30 bg-warn/10 px-2 py-1 text-[11px] text-warn" role="status">
                    Too much open work to count exactly — these are undercounts.
                  </div>
                )}
                <div className="max-h-[460px] overflow-y-auto rounded-lg border border-line bg-canvas p-2">
                  {/* The same doorway the tiles are: a workload row opens that
                      person's open work in Reports. */}
                  {data.workload.slice(0, 30).map((w) => (
                    <button type="button" key={w.assignee.id} onClick={() => openReports(undefined, { states: ['open'], assigneeId: w.assignee.id })} title={`${w.assignee.name} — open work, in Reports`} className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left transition hover:bg-hover">
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium text-white" style={{ backgroundColor: avatarColor(w.assignee.name) }}>{initials(w.assignee.name)}</span>
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{w.assignee.name}</span>
                      <span className="shrink-0 text-[11px] tabular-nums text-muted">{w.open}</span>
                      {w.overdue > 0 && <span className="shrink-0 rounded bg-danger/15 px-1 text-[10px] tabular-nums text-danger">{w.overdue} late</span>}
                    </button>
                  ))}
                  {data.unassignedOpen > 0 && (
                    <div className="mt-1 border-t border-line pt-1">
                      <button type="button" onClick={() => openReports(undefined, { states: ['open'], assigneeId: 'none' })} title="Open work nobody owns, in Reports" className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left transition hover:bg-hover">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hover text-[10px] text-faint">?</span>
                        <span className="min-w-0 flex-1 truncate text-[13px] text-faint">Unassigned</span>
                        <span className="shrink-0 rounded bg-warn-soft px-1 text-[10px] tabular-nums text-warn">{data.unassignedOpen}</span>
                      </button>
                    </div>
                  )}
                </div>
              </aside>
              )}
            </div>

            <section>
              <h2 className="mb-2 text-sm font-semibold text-ink">Projects</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.projects.map((p) => {
                  const pct = p.total ? Math.round((100 * p.done) / p.total) : 0;
                  return (
                    <button key={p.areaId} onClick={() => selectArea(p.areaId)} className="group flex flex-col rounded-xl border border-line bg-canvas p-3.5 text-left transition hover:border-(--color-line-strong) hover:bg-hover">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${p.isContentTeam ? 'bg-brand' : 'bg-(--color-area-dot)'}`} />
                        <span className="truncate text-[14px] font-medium text-ink">{p.name}</span>
                      </div>
                      <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-hover">
                        <div className="h-full rounded-full bg-(--color-progress)" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="mt-1.5 flex items-center justify-between text-[11px]">
                        {/* "incl. sub-tasks" is load-bearing, not decoration. This tile
                            counts every task in the project; the board it opens shows
                            top-level cards only (a kanban listing 1,161 Events cards
                            would be unusable). Without saying so, clicking a tile that
                            reads 1,161 and landing on 319 cards reads as a bug. */}
                        <span className="text-faint" title="Counts every task in this project, including sub-tasks">
                          {p.done}/{p.total} done · {pct}% <span className="text-[10px] opacity-60">incl. sub-tasks</span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="text-muted">{p.open} open</span>
                          {p.overdue > 0 && <span className="rounded bg-danger/15 px-1 text-danger">{p.overdue} late</span>}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
